import { Toster } from '@cleartax/wg-library-toast';
import * as Sentry from '@sentry/nextjs';
import { AxiosError } from 'axios';
import { TMPBackendError } from './axios';

/**
 * Custom error class for website errors that inherits from the built-in Error class.
 *
 * @extends {Error}
 */
export class WebsiteError extends Error {
  /**
   * Creates an instance of WebsiteError.
   *
   * @param {Error} error - The error that caused the website error.
   */
  constructor(error: Error) {
    super(error.message);
    this.name = error.name;
    this.cause = error.cause || null;
  }

  /**
   * Captures the error and sends it to Sentry.
   *
   * @returns {this}
   */
  capture() {
    Sentry.captureException(this, (scope) => {
      scope.setContext(
        'extra-data',
        this.cause as unknown as Record<string, unknown>
      );
      return scope;
    });
    return this;
  }

  /**
   * Displays the error message in a toaster component.
   *
   * @param {string} [message] - The message to display in the toaster. If not provided, the error's message will be used.
   * @returns {this}
   */
  withToaster(message?: string) {
    Toster.Error(message ? message : this.message);
    return this;
  }
}

export const uncaughtApiError = (err: AxiosError, statusCode?: number) => {
  const errorName = `Api Failed With ${
    statusCode ? statusCode + ' Response' : 'without response'
  } `;
  const error = new WebsiteError(err);
  error.name = errorName;
  error.cause = err.response || [];
  error.capture().withToaster('Server Error');
  new WebsiteError(error).capture();
};

export const somethingTerriblyWentWrong = (
  err: TMPBackendError,
  message?: string
) => {
  const errorName = 'Something terribly went wrong!';
  const error = new WebsiteError({
    message: err.message || 'ErrorMessage',
    name: err.errorObj.name || 'WebsiteError',
  });
  error.name = errorName;
  error.capture().withToaster('Server Error');
  if (message) Sentry.captureMessage(message);
};

export const handleMpBackendError = (err: TMPBackendError) => {
  const { errorObj } = err;
  if (!errorObj) {
    somethingTerriblyWentWrong(err);
    return;
  }
  if (errorObj.response && errorObj.response.status >= 500) {
    return;
  }
  if (errorObj.response) {
    uncaughtApiError(errorObj, errorObj.response.status);
    return;
  }
  if (errorObj.request) {
    uncaughtApiError(errorObj);
    return;
  }
  somethingTerriblyWentWrong(err);
};
